/** Formatting helpers shared across pages. */

/**
 * A one-line plain-text snippet for a card's description slot. Strips light
 * markdown, collapses whitespace/newlines, and truncates with an ellipsis so a
 * multi-line issue body renders as a single calm line.
 */
export function snippet(text: string | null | undefined, max = 140): string {
  if (!text) return '';
  const line = text
    .replace(/```[\s\S]*?```/g, ' ') // drop fenced code blocks
    .replace(/[`*_#>|]/g, '') // strip common markdown punctuation
    .replace(/\s+/g, ' ')
    .trim();
  return line.length > max ? line.slice(0, max - 1).trimEnd() + '…' : line;
}

export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const diffMs = Date.now() - then;
  const sec = Math.round(diffMs / 1000);
  const abs = Math.abs(sec);

  const units: Array<[number, Intl.RelativeTimeFormatUnit]> = [
    [60, 'second'],
    [3600, 'minute'],
    [86400, 'hour'],
    [604800, 'day'],
    [2629800, 'week'],
    [31557600, 'month'],
    [Infinity, 'year'],
  ];

  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  let divisor = 1;
  for (const [limit, unit] of units) {
    if (abs < limit) {
      const value = Math.round(-sec / divisor);
      return rtf.format(value, unit);
    }
    divisor = limit;
  }
  return '—';
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    // 24-hour clock everywhere, regardless of the viewer's locale. h23
    // guarantees 00–23 (hour12: false can map to h24 in some locales).
    hourCycle: 'h23',
  });
}

export function formatDuration(
  start: string | null | undefined,
  end: string | null | undefined,
): string {
  if (!start) return '—';
  const startMs = new Date(start).getTime();
  const endMs = end ? new Date(end).getTime() : Date.now();
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) return '—';
  let s = Math.max(0, Math.round((endMs - startMs) / 1000));
  const h = Math.floor(s / 3600);
  s -= h * 3600;
  const m = Math.floor(s / 60);
  s -= m * 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

export function formatCost(usd: number | null | undefined): string {
  if (usd === null || usd === undefined) return '—';
  return `$${usd.toFixed(usd < 1 ? 4 : 2)}`;
}

/** Format a raw token count as a human-readable string (e.g. 1500 → "1.5k"). */
export function formatTokens(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

export function formatSeconds(seconds: number | null | undefined): string {
  if (seconds == null) return '—';
  let s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  s -= h * 3600;
  const m = Math.floor(s / 60);
  s -= m * 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

export function titleCase(value: string): string {
  return value
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Human-readable file size: 512 B, 12 KB, 3.4 MB, 1.2 GB. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}
